<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type DownloadDriver, downloadDriversApi } from '@/api/entities/review/downloadDrivers'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'

definePage({
  meta: {
    title: '채널 관리',
    action: 'read',
    subject: 'review',
  },
})

const { snackbar, handleError } = useErrorHandler()

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const downloadDrivers = ref<DownloadDriver[]>([])
const driverConfigs = ref<DriverConfig[]>([])
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => Promise<void>) | null>(null)

// 필터
const searchQuery = ref('')
const filterStatus = ref<'all' | 'active' | 'inactive'>('all')

// 테이블 헤더
const headers = [
  { title: '사용', key: 'active', sortable: false, width: '80px' },
  { title: '이름', key: 'name', sortable: true },
  { title: '리뷰 예외 시작 날짜', key: 'skip_start_date', sortable: false },
  { title: '리뷰 예외 종료 날짜', key: 'skip_end_date', sortable: false },
  { title: '드라이버 설정', key: 'driver_settings', sortable: false, width: '280px' },
  { title: '', key: 'actions', sortable: false, width: '100px' },
]

// 테이블 아이템 (편집 가능하도록 ref로 변경)
interface DriverTableItem {
  download_driver_id: number
  driver_config_id: number | null
  driver_code: string
  name: string
  active: boolean
  skip_start_date: string | null
  skip_end_date: string | null
  driver_config: Record<string, unknown> | null
  includeExperience: boolean
  sellerNames: string[]
}

const tableItems = ref<DriverTableItem[]>([])

// 필터링된 테이블 아이템
const filteredTableItems = computed(() => {
  let items = [...tableItems.value]

  // 검색어 필터
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()

    items = items.filter(item => item.name.toLowerCase().includes(query))
  }

  // 상태 필터
  if (filterStatus.value === 'active')
    items = items.filter(item => item.active)
  else if (filterStatus.value === 'inactive')
    items = items.filter(item => !item.active)

  // 정렬: 사용 여부 > 예외 날짜 설정 여부 > 이름순
  items.sort((a, b) => {
    // 1. 사용 여부 (active가 먼저)
    if (a.active !== b.active)
      return a.active ? -1 : 1

    // 2. 예외 날짜 설정 여부 (설정된 것이 먼저)
    const aHasDate = !!(a.skip_start_date || a.skip_end_date)
    const bHasDate = !!(b.skip_start_date || b.skip_end_date)
    if (aHasDate !== bHasDate)
      return aHasDate ? -1 : 1

    // 3. 이름순
    return a.name.localeCompare(b.name, 'ko')
  })

  return items
})

// ISO 날짜 문자열을 YYYY-MM-DD 형식으로 변환
const formatDateForInput = (isoString: string | null): string | null => {
  if (!isoString)
    return null

  // ISO 문자열에서 날짜 부분만 추출 (YYYY-MM-DD)
  return isoString.substring(0, 10)
}

// 테이블 아이템 업데이트 함수
const parseSellerNames = (config: Record<string, unknown> | null): string[] => {
  if (!config) return []
  const names = config.sellerNames as string[] | undefined
  if (names) return [...names]
  const name = config.sellerName as string | string[] | undefined
  if (name) return Array.isArray(name) ? [...name] : [name]
  
  return []
}

const updateTableItems = () => {
  tableItems.value = downloadDrivers.value.map(driver => {
    const driverConfig = driverConfigs.value.find(config => config.driver_code === driver.code)

    return {
      download_driver_id: driver.id,
      driver_config_id: driverConfig?.id || null,
      driver_code: driver.code,
      name: driver.name,
      active: !!driverConfig,
      skip_start_date: formatDateForInput(driverConfig?.skipped_start_date || null),
      skip_end_date: formatDateForInput(driverConfig?.skipped_end_date || null),
      driver_config: driverConfig?.driver_config ? { ...driverConfig.driver_config } : null,
      includeExperience: (driverConfig?.driver_config as Record<string, unknown>)?.includeExperience === true,
      sellerNames: parseSellerNames(driverConfig?.driver_config as Record<string, unknown> | null),
    }
  })
}

// 데이터 로드
const loadDownloadDrivers = async () => {
  try {
    const { success, data } = await downloadDriversApi.listDownloadDrivers({
      driver_state: 'active',
      sort: 'name',
      per_page: 1000,
    })

    if (success && data)
      downloadDrivers.value = data.data || []
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 1000,
    })

    if (success && data)
      driverConfigs.value = data.data || []

    // 테이블 아이템 업데이트
    updateTableItems()
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

// 확인 다이얼로그
const showConfirm = (message: string, action: () => Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

const executeConfirm = async () => {
  if (confirmAction.value)
    await confirmAction.value()

  confirmDialog.value = false
}

// 드라이버 설정 저장
const saveDriverConfig = (item: DriverTableItem) => {
  if (item.active) {
    if (item.driver_config_id)
      updateDriverConfig(item)
    else
      createDriverConfig(item)
  }
  else if (item.driver_config_id) {
    deleteDriverConfig(item)
  }
}

const createDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널을 활성화 시키시겠습니까?`,
    async () => {
      if (!selectedClient.value)
        return

      const { success } = await driverConfigsApi.createDriverConfig(selectedClient.value.id, {
        download_driver_id: item.download_driver_id,
        skip_start_date: item.skip_start_date,
        skip_end_date: item.skip_end_date,
      })

      if (success) {
        snackbar.success(`${item.name} 채널이 활성화 되었습니다.`)
        await loadDriverConfigs()
      }
      else {
        snackbar.error(`${item.name} 채널을 활성화하지 못했습니다.`)
      }
    },
  )
}

const updateDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널 정보를 수정하시겠습니까?`,
    async () => {
      if (!selectedClient.value || !item.driver_config_id)
        return

      const updateData: Record<string, unknown> = {
        skip_start_date: item.skip_start_date,
        skip_end_date: item.skip_end_date,
      }

      // driver_config JSON 값 업데이트 — 변경된 필드만 전송하여 stale 데이터 덮어쓰기 방지
      if (item.driver_code === 'musinsa_v2') {
        updateData.driver_config = { includeExperience: item.includeExperience }
      }
      else if (['coupang_rocket', 'coupang_rocket_v2'].includes(item.driver_code)) {
        updateData.driver_config = { sellerNames: item.sellerNames }
      }

      const { success } = await driverConfigsApi.updateDriverConfig(
        selectedClient.value.id,
        item.driver_config_id,
        updateData,
      )

      if (success) {
        snackbar.success(`${item.name} 채널 정보가 수정되었습니다.`)
        await loadDriverConfigs()
      }
      else {
        snackbar.error(`${item.name} 채널 정보를 수정하지 못했습니다.`)
      }
    },
  )
}

const deleteDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널을 비활성화 시키시겠습니까? 해당 채널의 수집된 상품, 리뷰, 그리고 매핑 데이터가 삭제 됩니다.`,
    async () => {
      if (!selectedClient.value || !item.driver_config_id)
        return

      const { success } = await driverConfigsApi.deleteDriverConfig(
        selectedClient.value.id,
        item.driver_config_id,
      )

      if (success) {
        snackbar.success(`${item.name} 채널을 비활성화 시켰습니다.`)
        await loadDriverConfigs()
      }
      else {
        snackbar.error(`${item.name} 채널을 비활성화 시키지 못했습니다.`)
      }
    },
  )
}

// 감시자
watch(selectedClient, async () => {
  if (selectedClient.value) {
    await loadDownloadDrivers()
    await loadDriverConfigs()
  }
  else {
    downloadDrivers.value = []
    driverConfigs.value = []
    tableItems.value = []
  }
})

onMounted(async () => {
  if (selectedClient.value) {
    await loadDownloadDrivers()
    await loadDriverConfigs()
  }
})
</script>

<template>
  <div>
    <!-- 상단 섹션 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <ClientSelector />
      </VCol>
      <VCol
        cols="12"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <p class="text-subtitle-2 mb-1">
                사용 채널 수
              </p>
              <h4 class="text-h4">
                {{ driverConfigs.length }}
              </h4>
            </div>
            <VAvatar
              color="info"
              variant="tonal"
              rounded
            >
              <VIcon icon="tabler-server" />
            </VAvatar>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 채널 목록 -->
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-4">
        <span>채널 목록</span>
        <div class="d-flex align-center gap-4">
          <VTextField
            v-model="searchQuery"
            placeholder="채널명 검색"
            density="compact"
            variant="outlined"
            prepend-inner-icon="tabler-search"
            hide-details
            style="min-width: 200px"
            clearable
          />
          <VBtnToggle
            v-model="filterStatus"
            mandatory
            density="compact"
            color="primary"
          >
            <VBtn
              value="all"
              size="small"
            >
              전체
            </VBtn>
            <VBtn
              value="active"
              size="small"
            >
              사용중
            </VBtn>
            <VBtn
              value="inactive"
              size="small"
            >
              미사용
            </VBtn>
          </VBtnToggle>
        </div>
      </VCardTitle>
      <VCardText>
        <VDataTable
          v-if="tableItems.length > 0"
          :headers="headers"
          :items="filteredTableItems"
          :loading="loading"
          class="elevation-0"
          :row-props="({ item }) => ({ class: item.active ? 'bg-success-lighten' : '' })"
        >
          <!-- 사용 여부 -->
          <template #item.active="{ item }">
            <VSwitch
              v-model="item.active"
              hide-details
              color="success"
            />
          </template>

          <!-- 예외 시작 날짜 -->
          <template #item.skip_start_date="{ item }">
            <VTextField
              v-model="item.skip_start_date"
              type="date"
              density="compact"
              hide-details
              variant="outlined"
            />
          </template>

          <!-- 예외 종료 날짜 -->
          <template #item.skip_end_date="{ item }">
            <VTextField
              v-model="item.skip_end_date"
              type="date"
              density="compact"
              hide-details
              variant="outlined"
            />
          </template>

          <!-- 드라이버 설정 -->
          <template #item.driver_settings="{ item }">
            <template v-if="item.active">
              <VSelect
                v-if="item.driver_code === 'musinsa_v2'"
                v-model="item.includeExperience"
                :items="[
                  { title: '체험단 제외', value: false },
                  { title: '체험단 포함', value: true },
                ]"
                density="compact"
                hide-details
                variant="outlined"
                label="체험단 리뷰"
              />
              <div v-else-if="['coupang_rocket', 'coupang_rocket_v2'].includes(item.driver_code)">
                <VCombobox
                  v-model="item.sellerNames"
                  :items="[]"
                  multiple
                  chips
                  closable-chips
                  density="compact"
                  hide-details
                  variant="outlined"
                  label="판매자 필터"
                  placeholder="판매자명 입력 후 Enter"
                />
                <small class="text-medium-emphasis">쿠팡(주)는 자동 포함</small>
              </div>
              <span
                v-else
                class="text-medium-emphasis"
              >-</span>
            </template>
          </template>

          <!-- 작업 -->
          <template #item.actions="{ item }">
            <VBtn
              variant="outlined"
              color="primary"
              size="small"
              @click="saveDriverConfig(item)"
            >
              저장
            </VBtn>
          </template>
        </VDataTable>

        <p
          v-else
          class="text-center py-8"
        >
          고객사를 선택해주세요.
        </p>
      </VCardText>
    </VCard>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="executeConfirm"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.bg-success-lighten {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}
</style>
